import { FunctionComponent, createContext, useState, useEffect, useContext, } from 'react';
import classnames from 'classnames';
import { addPetPath } from 'Src/constants/paths';
import { ADD_PET_POPUP_ID } from 'Src/constants/popup';
import Popup from '@media-ui/core/src/components/Popup';
import { usePopup } from '@media-ui/core/src/components/Popup/Popup.hooks';
import { useForm, FormProvider } from 'react-hook-form';
import transport from '@media-ui/core/src/tools/transport';
import { getAccountInfoRequest } from '@media-platform/tool-auth/dist/utils';
import auth from 'Tools/auth';
import removeQueries from 'Tools/removeQueries';
import getByPath from 'Tools/getByPath';
import { snitchCounter } from 'Tools/counters/snitchCounter';
import { PET_NAMES } from 'Tools/pets';
import getAddPetFormOpenType from 'Tools/getAddPetFormOpenType';
import * as s from './AddPet.css';
import * as footerStyles from './Footer/Footer.css';
import * as T from './AddPet.types';
import Footer from './Footer';
import Body from './Body';
import MailIcon from './img/mail.svg';
import { getDefaultValuesFromRemote, getStatBannerId } from './utils';
import { pushTmr } from 'Src/tools/counters/pushTmr';
import type { IServerDataBase } from 'Src/typings/context';
const StepHeader: FunctionComponent<{
    step: number;
}> = ({ step }) => {
    const mailIconClasses = classnames(s.icon, s.mailIcon);
    const text = step === 0
        ? 'Дарим скидку 15% на заказ продукции Purina  в онлайн-магазине Petshop.ru за регистрацию питомца!'
        : 'Спасибо!';
    const textClasses = classnames(s.text, {
        [s.firstText]: step === 0,
        [s.finalText]: step === 3,
    });
    return (<div className={s.StepHeader}>
      <div className={s.petsshopIcon}></div>
      {step === 3 && (<div className={mailIconClasses}>
          <MailIcon />
        </div>)}
      <div className={textClasses}>{text}</div>
    </div>);
};
const year2022Steps = [
    '',
    'Добавьте своего питомца',
    'Расскажите подробнее о питомце',
    'Расскажите немного о себе',
    'Питомец добавлен!',
];
const petsshopSteps = [
    <StepHeader step={0}/>,
    'Расскажите подробнее о питомце',
    'Расскажите немного о себе',
    <StepHeader step={3}/>,
];
const defaultSteps = [
    'Выберите своего питомца',
    'Расскажите подробнее о питомце',
    'Расскажите немного о себе',
    'Питомец добавлен!',
];
const steps = {
    default: defaultSteps,
    petsshop: petsshopSteps,
    year2022: year2022Steps,
};
const getHeaderText = (step: number, viewType: string | undefined, isEdit: boolean) => {
    if (!viewType) {
        return null;
    }
    const editSteps = ['Расскажите подробнее о питомце', 'Удалить питомца?'];
    const createSteps = steps[viewType as keyof typeof steps];
    return isEdit ? editSteps[step] : createSteps[step];
};
export const AddPetContext = createContext<T.IAddPetContext | undefined>(undefined);
let isPetAdded = false;
let isPetChanged = false;
let petType: number | null = null;
const AddPetPopup: FunctionComponent<{
    context: IServerDataBase;
}> = ({ context, }) => {
    const [step, setStep] = useState<number>(0);
    const [remoteAddPetContext, setRemoteAddPetContext] = useState<T.IAddPetContext>();
    const isTouch = getByPath('layout.custom.isMobile', context);
    const crm_id = getByPath('layout.custom.crm_id', context);
    const actions = getByPath('layout.custom.actions', context);
    const petsshopPromoRules = getByPath('layout.custom.petsshop_promo_rules', context);
    const isAddPetPurinaRequired = actions && actions.includes('add_pet_purina_required');
    let PH: T.IPH;
    const openAddPetDefaultProps: T.IOpenAddPetArgs = {
        url: addPetPath,
        pet: null,
        isPetsshop: false,
        year2022: false,
    };
    const { show, hide, activeId } = usePopup(ADD_PET_POPUP_ID, {
        clearQueue: true,
        onHide: ({ id }) => {
            if (id !== ADD_PET_POPUP_ID) {
                return null;
            }
            const isAddingPet = (isPetAdded || isPetChanged) && window;
            if (isAddingPet) {
                window.location.href = removeQueries(window.location.href, [
                    'add-pet',
                    'from',
                ]);
            }
            setStep(0);
        },
    });
    if (remoteAddPetContext?.petHadChanged) {
        isPetChanged = true;
    }
    const openAddPet = async (props: T.IOpenAddPetArgs) => {
        const { url, pet = 1, isPetsshop, year2022, viewType: view, forceType, } = {
            ...openAddPetDefaultProps,
            ...props,
        };
        const activeUser = await getAccountInfoRequest();
        const isEdit = Boolean(/edit/.exec(url || ''));
        if (activeUser.status !== 'ok') {
            auth();
            return;
        }
        const formContext = await transport(url || '');
        const birthDateValue = getByPath('form.fields.birth_date.value', formContext);
        if (typeof birthDateValue === 'string' && birthDateValue.includes('-')) {
            formContext.form.fields.birth_date.value = birthDateValue.replaceAll('-', '/');
        }
        const isPetsshopValue = (actions &&
            actions.includes('petshopru_2021') &&
            typeof crm_id !== 'string') ||
            isPetsshop;
        const year2022ViewType = year2022 ? 'year2022' : 'default';
        const viewType = isPetsshop ? 'petsshop' : year2022ViewType;
        const statBannerId = await getStatBannerId();
        if (statBannerId) {
            formContext.form.fields.rb_banner_id.value = statBannerId;
        }
        setRemoteAddPetContext({
            remote: formContext,
            mainUrl: url,
            setStep,
            step,
            isEdit,
            petHadChanged: false,
            hideWindow: hide,
            setContext: setRemoteAddPetContext,
            isTouch: isTouch,
            isPetsshop: isPetsshopValue,
            isAddPetPurinaRequired,
            petsshopPromoRules: petsshopPromoRules,
            isError: false,
            errorMessage: null,
            viewType,
            view: view || 'default',
            statBannerId,
        });
        isPetAdded = false;
        if (pet !== null) {
            petType = pet;
            snitchCounter('addPetFormTypePick', {
                petType: PET_NAMES[pet as unknown as keyof typeof PET_NAMES],
            });
        }
        if (activeId)
            hide();
        show();
        snitchCounter('addPetFormOpen', {
            type: getAddPetFormOpenType(Number(view), Number(petType)) ||
                forceType ||
                'default',
        });
        if (isEdit) {
            snitchCounter('editPetFormOpen');
        }
        pushTmr({
            id: '2958944',
            type: 'reachGoal',
            goal: isTouch ? 'popup_pets_touch' : 'popup_pets_web',
        });
    };
    const nextStep = (): void => {
        setStep(step + 1);
        if (step === 2) {
            isPetAdded = true;
        }
    };
    const prevStep = (): void => {
        if (step === 0)
            hide();
        setStep(step - 1);
    };
    useEffect(() => {
        if (!PH)
            PH = window.__PH;
        if (!window.pets)
            window.pets = {
                openAddPet: openAddPet,
            };
        const params = new URLSearchParams(window.location.search);
        const hasCat = params.has('add-pet-side-cat');
        const hasWoman = params.has('add-pet-side-woman');
        const hasPetsshop = params.has('add-pet-petsshop');
        const forceOpen = params.has('add-pet') || hasPetsshop || hasWoman || hasCat;
        if (forceOpen) {
            openAddPet({
                forceType: hasCat ? 'sideCatWidget' : 'sideWomanWidget',
                isPetsshop: hasPetsshop,
                pet: petType,
                url: addPetPath,
            });
        }
    }, []);
    if (!remoteAddPetContext) {
        return null;
    }
    return (<AddPetContext.Provider value={remoteAddPetContext}>
      <AddPet key={remoteAddPetContext?.mainUrl} prevStep={prevStep} nextStep={nextStep} step={step} isTouch={isTouch} isAddPetPurinaRequired={isAddPetPurinaRequired}/>
    </AddPetContext.Provider>);
};
const AddPet: FunctionComponent<T.IAddPetProps> = ({ prevStep, nextStep, step, isTouch, isAddPetPurinaRequired, }) => {
    const addPetContext = useContext(AddPetContext);
    const hookFormMethods = useForm({
        defaultValues: getDefaultValuesFromRemote(addPetContext?.remote),
    });
    const maxYear2022 = addPetContext?.viewType === 'year2022' ? 4 : 3;
    const maxSteps = addPetContext?.isEdit ? 2 : maxYear2022;
    const popupFooterClasses = classnames(footerStyles.popupFooter, {
        [footerStyles.petsshopPopupFooter]: addPetContext?.isPetsshop,
        [footerStyles.petsshopPopupFooterFirst]: addPetContext?.isPetsshop && step === 0,
        [footerStyles.popupFooterYear2022]: addPetContext?.viewType === 'year2022' && (step === 0 || step === 1),
    });
    const headerClasses = classnames(s.header, {
        [s.headerPetsshop]: addPetContext?.isPetsshop,
        [s.headerYear2022]: addPetContext?.viewType === 'year2022' && step === 1,
    });
    const { watch, getValues, register, setValue } = hookFormMethods;
    const size = addPetContext?.viewType === 'year2022' && (step === 0 || step === 1)
        ? 'small'
        : 'medium';
    const header = addPetContext?.isError
        ? 'Ошибка!'
        : getHeaderText(step, addPetContext?.viewType, addPetContext?.isEdit || false);
    watch();
    const FIELD_NAME = 'pet';
    const valueFromForm = getValues(FIELD_NAME);
    if (petType) {
        if (!valueFromForm) {
            register({ name: FIELD_NAME, type: 'custom' }, { required: true });
        }
        setValue(FIELD_NAME, String(petType));
        nextStep();
        petType = null;
    }
    return (<FormProvider {...hookFormMethods}>
      <Popup dataQa="AddPetPopup" bodyClassName={s.body} className={s.popupRoot} headerClassName={headerClasses} header={header} footerClassName={popupFooterClasses} size={size} footer={step < maxSteps && (<Footer next={nextStep} prev={prevStep} step={step} isTouch={isTouch || false} isAddPetPurinaRequired={isAddPetPurinaRequired}></Footer>)} id={ADD_PET_POPUP_ID}>
        <Body step={step}/>
      </Popup>
    </FormProvider>);
};
export default AddPetPopup;
